import React, { useEffect, useMemo, useState } from 'react';
import {
  LayoutChangeEvent,
  StyleSheet,
  Text,
  View,
  Platform,
  TouchableOpacity,
} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import LinearGradient from 'react-native-linear-gradient';
import { PieChart } from 'react-native-gifted-charts';
import SkeletonPlaceholder from 'react-native-skeleton-placeholder';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { useTheme, GTFonts, type Theme } from '../../theme';
import { fs, formatWithCommas, iconSize, s, v } from '../../utils/libs';
import {
  formatSharePercent,
  getCollectionPace,
  sortMethodsByNetDesc,
} from '../../utils/paymentBreakdown';
import { ComparisonChip } from '../Reusables/ComparisonChip';
import CollectionPaceCard from './CollectionPaceCard';
import GreenActionButton from '../Reusables/GreenActionButton';
import type {
  PaymentMethodBreakdown,
  PaymentMethodRow,
} from '../../redux/backoffice/dashboardSlice';

const isWeb = Platform.OS === 'web';

type TargetProps = {
  /** The persisted target amount, or null/0 when none is set. */
  targetAmount: number | null;
  /** Current month's cumulative sales. */
  currentMonthlySales: number;
  /** Whether target data is still loading (first fetch). */
  targetLoading?: boolean;
  /** Callback to open the "Set Target" dialog. */
  onAddTarget: () => void;
};

type Props = {
  data: PaymentMethodBreakdown | null;
  loading?: boolean;
  /** Fetch error. Shown instead of an endless skeleton when nothing loaded. */
  error?: string | null;
  currencyText: string;
  /** Shared method -> color map so this card, the mix card and the chart agree. */
  methodColors: Record<string, string>;
  /** Comparison label used in the "vs …" caption, e.g. "last month". */
  comparisonPeriodLabel?: string;
  /** Optional target props — when provided, renders the monthly target footer
   *  inside this card (native only). On web the featured section is separate. */
  target?: TargetProps;
};

const MethodRow: React.FC<{
  row: PaymentMethodRow;
  color: string;
  currencyText: string;
  /** Share of the period's collections, 0..1. */
  share: number;
  styles: ReturnType<typeof createStyles>;
}> = ({ row, color, currencyText, share, styles }) => (
  <View style={styles.methodRow}>
    <View style={[styles.dot, { backgroundColor: color }]} />
    <Text style={styles.methodName} numberOfLines={1}>
      {row.method}
    </Text>
    <Text style={styles.methodAmount}>
      {currencyText} {formatWithCommas(row.net, 0)}
    </Text>
    <Text style={styles.methodShare}>{formatSharePercent(share)}</Text>
  </View>
);

const MoneyCollectedCard: React.FC<Props> = ({
  data,
  loading = false,
  error,
  currencyText,
  methodColors,
  comparisonPeriodLabel,
  target,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [paceExpanded, setPaceExpanded] = useState(false);

  // Only methods that actually took money get a slice and a row. A method
  // sitting at zero is noise, not information.
  const rows = useMemo<PaymentMethodRow[]>(
    () => sortMethodsByNetDesc(data?.by_method).filter(r => Number(r.net) > 0),
    [data],
  );
  const displayMethodColors = useMemo(
    () =>
      rows.length === 1
        ? {
            ...methodColors,
            [rows[0].method]: theme.colors.successEmerald,
          }
        : methodColors,
    [methodColors, rows, theme.colors.successEmerald],
  );

  const collectedNet = Number(data?.totals?.collected_net || 0);
  const tenderCount = Number(data?.totals?.tender_count || 0);
  // The API calls this "refunded"; in this POS it is change handed back when a
  // customer cannot pay to the exact shilling.
  const changeGiven = Number(data?.totals?.refunded || 0);
  const changeCount = useMemo(
    () =>
      (data?.by_method || []).reduce(
        (acc, row) => acc + Number(row?.refund_count || 0),
        0,
      ),
    [data],
  );
  const realisedSales = Number(data?.reconciliation?.realisedSales || 0);
  // Derived rather than read off `reconciliation.delta`, so the copy can never
  // be inverted by the API's sign convention. Positive means sales outran
  // collections, i.e. money is still to come in.
  const uncollected = realisedSales - collectedNet;
  const netComparison = data?.comparison?.totals?.collected_net;

  const pieData = useMemo(
    () =>
      rows.map(row => ({
        value: Number(row.net || 0),
        color: displayMethodColors[row.method] || theme.colors.primary,
      })),
    [rows, displayMethodColors, theme.colors.primary],
  );

  // Fall back to deriving the share when the API omits share_net, so a row
  // never silently reads 0%.
  const shareOf = (row: PaymentMethodRow): number => {
    const share = Number(row.share_net);
    if (Number.isFinite(share) && share > 0) return share;
    return collectedNet > 0 ? Number(row.net || 0) / collectedNet : 0;
  };

  // Collections as a share of sales realised in the same window. Can exceed
  // 100% when the period collects money owed on older orders.
  const collectionRate = realisedSales > 0 ? collectedNet / realisedSales : null;

  // Only surface the gap when it is big enough to be a real difference rather
  // than rounding between the two aggregations.
  const showReconciliation =
    realisedSales > 0 &&
    Math.abs(uncollected) >= 1 &&
    Math.abs(uncollected) / realisedSales >= 0.005;

  const hasPace = useMemo(() => Boolean(getCollectionPace(data)), [data]);

  const hasFailed = !!error && !data;
  // Only skeleton when we truly have nothing to render. Refetches keep the
  // last payload on screen — same contract as StatCard's `loading && !totals`.
  const isLoading = !hasFailed && !data && loading;
  const isEmpty = !isLoading && !hasFailed && !!data && collectedNet <= 0 && tenderCount <= 0;

  const renderSkeleton = (width: number, height: number, radius = s(4)) => (
    <SkeletonPlaceholder
      backgroundColor={theme.colors.border}
      highlightColor={theme.colors.surfaceSecondary}>
      <SkeletonPlaceholder.Item
        width={width}
        height={height}
        borderRadius={radius}
      />
    </SkeletonPlaceholder>
  );

  return (
    <View style={styles.card}>
      <View style={styles.headerRow}>
        <Text style={styles.title}>Money Collected</Text>
      </View>

      {hasFailed ? (
        <View style={styles.emptyWrap}>
          <MaterialCommunityIcons
            name="alert-circle-outline"
            size={fs(24)}
            color={theme.colors.textTertiary}
          />
          <Text style={styles.emptyText}>
            Payments could not be loaded right now
          </Text>
        </View>
      ) : isEmpty ? (
        <View style={styles.emptyWrap}>
          <MaterialCommunityIcons
            name="wallet-outline"
            size={fs(24)}
            color={theme.colors.textTertiary}
          />
          <Text style={styles.emptyText}>
            No payments taken in this period
          </Text>
        </View>
      ) : (
        <>
          <View style={styles.summaryRow}>
            {/* No slices means the period has a tender count but nothing
                attributable to a method — show the figures, skip the ring. */}
            {isLoading || pieData.length > 0 ? (
              <View style={styles.donutWrap}>
                {isLoading ? (
                  renderSkeleton(DONUT_SIZE, DONUT_SIZE, DONUT_SIZE / 2)
                ) : (
                  <PieChart
                    data={pieData}
                    donut
                    isAnimated
                    radius={DONUT_RADIUS}
                    innerRadius={DONUT_INNER_RADIUS}
                    strokeWidth={2}
                    strokeColor={theme.colors.surface}
                    innerCircleColor={theme.colors.surface}
                    backgroundColor={theme.colors.surface}
                    centerLabelComponent={() => (
                      <View style={styles.donutCenter}>
                        <Text style={styles.donutCenterValue}>
                          {collectionRate !== null
                            ? `${Math.round(collectionRate * 100)}%`
                            : formatWithCommas(tenderCount, 0)}
                        </Text>
                        <Text style={styles.donutCenterLabel}>
                          {collectionRate !== null
                            ? 'of sales'
                            : tenderCount === 1
                              ? 'payment'
                              : 'payments'}
                        </Text>
                      </View>
                    )}
                  />
                )}
              </View>
            ) : null}

            <View style={styles.summaryTextWrap}>
              {isLoading ? (
                renderSkeleton(s(130), v(28))
              ) : (
                <Text style={styles.amount} numberOfLines={1}>
                  {currencyText} {formatWithCommas(collectedNet, 0)}
                </Text>
              )}
              {netComparison ? (
                <ComparisonChip
                  comparison={netComparison}
                  periodLabel={comparisonPeriodLabel}
                  style={styles.comparison}
                />
              ) : null}
              {isLoading ? (
                <View style={{ marginTop: v(6) }}>
                  {renderSkeleton(s(120), v(14))}
                </View>
              ) : (
                <Text style={styles.meta}>
                  {formatWithCommas(tenderCount, 0)}{' '}
                  {tenderCount === 1 ? 'payment' : 'payments'}
                  {rows.length
                    ? ` · ${rows.length} ${
                        rows.length === 1 ? 'method' : 'methods'
                      }`
                    : ''}
                </Text>
              )}
            </View>
          </View>

          {isLoading || rows.length > 0 ? (
            <>
         
              <Text style={styles.sectionLabel}>Payment Methods</Text>  
                 {/* <View style={styles.divider} /> */}
            </>
          ) : null}

          {isLoading ? (
            <View>
              {[0, 1, 2].map(i => (
                <View key={i} style={styles.methodRow}>
                  {renderSkeleton(s(10), s(10), s(5))}
                  <View style={{ flex: 1, marginLeft: s(9) }}>
                    {renderSkeleton(s(70), v(14))}
                  </View>
                  {renderSkeleton(s(64), v(14))}
                </View>
              ))}
            </View>
          ) : (
            rows.map(row => (
              <MethodRow
                key={row.method}
                row={row}
                color={displayMethodColors[row.method] || theme.colors.primary}
                currencyText={currencyText}
                share={shareOf(row)}
                styles={styles}
              />
            ))
          )}

          {/* Monthly Target footer — Variation A: stacked below methods */}
          {!isWeb && target ? (
            <TargetFooter
              target={target}
              currencyText={currencyText}
              theme={theme}
              styles={styles}
            />
          ) : null}

          {/* Collection pace accordion — parked for now.
          {!isLoading && hasPace ? (
            <>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={
                  paceExpanded ? 'Hide collection pace' : 'Show collection pace'
                }
                onPress={() => setPaceExpanded(prev => !prev)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                style={styles.paceToggle}>
                <View style={styles.paceToggleLeft}>
                  <Text style={styles.paceToggleLabel}>Collection pace</Text>
                  {comparisonPeriodLabel ? (
                    <Text style={styles.paceToggleHint}>
                      vs {comparisonPeriodLabel}
                    </Text>
                  ) : null}
                </View>
                <MaterialCommunityIcons
                  name={paceExpanded ? 'chevron-up' : 'chevron-down'}
                  size={iconSize(18)}
                  color={theme.colors.text}
                />
              </TouchableOpacity>
              {paceExpanded ? (
                <CollectionPaceCard
                  data={data}
                  loading={loading}
                  currencyText={currencyText}
                  methodColors={displayMethodColors}
                  comparisonPeriodLabel={comparisonPeriodLabel}
                />
              ) : null}
            </>
          ) : null}
          */}
        </>
      )}

      {/* Target footer for empty/error states — the one inside the <>
          block above handles the normal case. */}
      {!isWeb && target && (hasFailed || isEmpty) ? (
        <TargetFooter
          target={target}
          currencyText={currencyText}
          theme={theme}
          styles={styles}
        />
      ) : null}
    </View>
  );
};

const TARGET_BAR_HEIGHT = 14;

const clamp = (value: number, min: number, max: number) => {
  'worklet';
  return Math.min(Math.max(value, min), max);
};

/** Animated progress bar matching MonthlySalesGoal's gradient style. */
const TargetFooter: React.FC<{
  target: TargetProps;
  currencyText: string;
  theme: Theme;
  styles: ReturnType<typeof createStyles>;
}> = ({ target, currencyText, theme, styles }) => {
  const {
    targetAmount,
    currentMonthlySales,
    targetLoading = false,
    onAddTarget,
  } = target;

  const hasTarget = (targetAmount ?? 0) > 0;
  const ratio = hasTarget
    ? clamp(currentMonthlySales / targetAmount!, 0, 1)
    : 0;
  const percent = Math.round(ratio * 100);

  const progress = useSharedValue(0);
  const trackWidth = useSharedValue(0);
  const [gradientWidth, setGradientWidth] = useState(0);

  useEffect(() => {
    progress.value = withTiming(ratio, {
      duration: 900,
      easing: Easing.out(Easing.cubic),
    });
  }, [ratio, progress]);

  const onTrackLayout = (e: LayoutChangeEvent) => {
    const width = e.nativeEvent.layout.width;
    trackWidth.value = width;
    setGradientWidth(width);
  };

  const fillStyle = useAnimatedStyle(() => ({
    width: trackWidth.value * progress.value,
  }));

  return (
    <>
      <View style={styles.targetDivider} />
      {hasTarget ? (
        <View style={styles.targetSection}>
          <View style={styles.targetHeaderRow}>
            <Text style={styles.targetLabel}>Monthly Target</Text>
            <Text
              style={[
                styles.targetPercent,
                percent >= 100 && { color: theme.colors.successEmerald },
              ]}>
              {percent}%
            </Text>
          </View>
          <View style={styles.targetTrack} onLayout={onTrackLayout}>
            <Animated.View style={[styles.targetFill, fillStyle]}>
              {gradientWidth > 0 && (
                <LinearGradient
                  colors={[theme.colors.primaryDark, theme.colors.success]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={{ width: gradientWidth, height: '100%' }}
                />
              )}
            </Animated.View>
          </View>
          <View style={styles.targetFooterRow}>
            <Text style={styles.targetFooterText}>
              Current: {currencyText} {formatWithCommas(currentMonthlySales, 0)}
            </Text>
            <Text
              style={[
                styles.targetFooterText,
                { color: theme.colors.successEmerald },
              ]}>
              Target: {currencyText} {formatWithCommas(targetAmount!, 0)}
            </Text>
          </View>
        </View>
      ) : (
        <View style={styles.targetSetRow}>
          <Text style={styles.targetLabel}>Monthly Target</Text>
          <GreenActionButton
            amount={targetAmount || 0}
            label="Set A Target"
            icon="add"
            onPress={onAddTarget}
            loading={targetLoading && targetAmount == null}
          />
        </View>
      )}
    </>
  );
};

const DONUT_RADIUS = v(52);
const DONUT_INNER_RADIUS = v(35);
const DONUT_SIZE = DONUT_RADIUS * 2;

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      marginTop: v(10),
      marginHorizontal: s(10),
      paddingHorizontal: s(16),
      paddingVertical: s(14),
      ...theme.shadows.sm,
    },
    headerRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(18),
      lineHeight: fs(20),
    },
    summaryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(14),
      marginTop: v(10),
    },
    donutWrap: {
      width: DONUT_SIZE,
      height: DONUT_SIZE,
      alignItems: 'center',
      justifyContent: 'center',
    },
    donutCenter: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    donutCenterValue: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(20),
      textAlign: 'center',
    },
    donutCenterLabel: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(10),
      lineHeight: fs(13),
      textAlign: 'center',
    },
    summaryTextWrap: {
      flex: 1,
      minWidth: 0,
    },
    amount: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: isWeb ? fs(28) : fs(24),
      lineHeight: isWeb ? fs(32) : fs(28),
    },
    comparison: {
      marginTop: v(6),
    },
    meta: {
      color: theme.colors.text,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      marginTop: v(4),
    },
    divider: {
      height: 1,
      backgroundColor: theme.colors.border,
      marginTop: v(12),
      marginBottom: v(8),
    },
    sectionLabel: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(13),
      lineHeight: fs(18),
      marginTop: v(12),
      marginBottom: v(2),
    },
    methodRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: v(6),
    },
    dot: {
      width: s(10),
      height: s(10),
      borderRadius: s(5),
    },
    methodName: {
      flex: 1,
      minWidth: 0,
      marginLeft: s(9),
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(18),
    },
    methodAmount: {
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(18),
    },
    methodShare: {
      width: s(46),
      textAlign: 'right',
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(18),
    },
    footnote: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(15),
      marginTop: v(8),
    },
    paceToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: v(12),
      paddingTop: v(10),
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
    },
    paceToggleLeft: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: s(8),
      rowGap: v(2),
    },
    paceToggleLabel: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
    paceToggleHint: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.semibold,
      fontSize: fs(11),
      lineHeight: fs(15),
    },
    emptyWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: v(22),
      rowGap: v(6),
    },
    emptyText: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
    // ── Target footer ──────────────────────────
    targetDivider: {
      height: 1,
      backgroundColor: theme.colors.border,
      marginTop: v(10),
      marginBottom: v(10),
    },
    targetSection: {
      gap: v(6) as unknown as number,
    },
    targetHeaderRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    targetLabel: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
    targetPercent: {
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
    targetTrack: {
      height: TARGET_BAR_HEIGHT,
      width: '100%',
      borderRadius: TARGET_BAR_HEIGHT / 2,
      backgroundColor: theme.colors.border,
      overflow: 'hidden',
    },
    targetFill: {
      height: '100%',
      borderRadius: TARGET_BAR_HEIGHT / 2,
      overflow: 'hidden',
    },
    targetFooterRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    targetFooterText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    targetSetRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  });

export default MoneyCollectedCard;
